<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <v-row>
                <v-col cols="12" sm="12" md="8">
                    <h1>Calculate Gross Profit by date range</h1>
                </v-col>
                <v-col cols="12" sm="12" md="4"> </v-col>
            </v-row>
            <v-expansion-panels v-model="data.panel" multiple>
                <v-expansion-panel>
                    <v-expansion-panel-title>
                        <h2>Select date range</h2>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <v-row>
                            <date-range-select
                                v-model="data.calculateGrossProfitRequest.dateOrderOn"
                                :from-label="formLabelText.dateOrderOnFrom"
                                :to-label="formLabelText.dateOrderOnTo"
                                :enable-validation="true"
                                @validation-change="onDateRangeValidationChange"
                            />
                        </v-row>
                        <v-row class="mt-2">
                            <v-btn class="mr-2" variant="text" @click="onResetClick">
                                <v-icon>mdi-cached</v-icon>
                                Clear
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn class="mr-2" variant="text" @click="onCalculateClick">
                                <v-icon>mdi-database-sync</v-icon>
                                Calculate GP
                            </v-btn>
                        </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
            <alert-message ref="errorAlert" :message="data.errorAlertMessage"></alert-message>
            <v-card>
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="6">
                            <v-btn class="mt-2" variant="text" @click="onListGrossProfitClick"> Go to list </v-btn>
                        </v-col>
                        <v-col cols="12" sm="12" md="6">
                            <v-spacer></v-spacer>
                        </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <!-- when calculate submitted, show processing message -->
                    <div v-if="data.processing" class="processing-section">
                        <v-progress-circular indeterminate color="primary" class="mr-3"></v-progress-circular>
                        <span class="processing-text"> Processing gross profit calculations... </span>
                    </div>

                    <!-- when calculate success, show comprehensive statistics -->
                    <div v-if="data.resultSuccess" class="success-section">
                        <v-alert
                            :type="
                                data.calculationResult.status === 'FULL_SUCCESS'
                                    ? 'success'
                                    : data.calculationResult.status === 'PARTIAL_SUCCESS'
                                    ? 'warning'
                                    : 'error'
                            "
                            variant="tonal"
                            class="mb-4"
                        >
                            <v-alert-title>
                                <v-chip
                                    :color="
                                        data.calculationResult.status === 'FULL_SUCCESS'
                                            ? 'success'
                                            : data.calculationResult.status === 'PARTIAL_SUCCESS'
                                            ? 'warning'
                                            : 'error'
                                    "
                                    size="small"
                                    class="mr-2"
                                >
                                    {{ data.calculationResult.status }}
                                </v-chip>
                                {{
                                    data.calculationResult.status === 'FULL_SUCCESS'
                                        ? 'All calculations completed successfully'
                                        : data.calculationResult.status === 'PARTIAL_SUCCESS'
                                        ? 'Calculations partially completed - some errors occurred'
                                        : 'Calculation failed'
                                }}
                            </v-alert-title>
                        </v-alert>

                        <!-- Critical Errors Section -->
                        <div
                            v-if="
                                data.calculationResult.criticalErrors &&
                                data.calculationResult.criticalErrors.length > 0
                            "
                            class="critical-errors-section mb-4"
                        >
                            <v-expansion-panels variant="accordion">
                                <v-expansion-panel>
                                    <v-expansion-panel-title class="text-error">
                                        <v-icon class="mr-2">mdi-alert-circle-outline</v-icon>
                                        Critical Errors ({{ data.calculationResult.criticalErrors.length }})
                                    </v-expansion-panel-title>
                                    <v-expansion-panel-text>
                                        <v-alert type="error" variant="outlined" class="mb-2">
                                            <div class="text-body-2">
                                                The following critical errors prevented some days from being processed:
                                            </div>
                                        </v-alert>
                                        <div class="critical-errors-list">
                                            <v-card
                                                v-for="(error, index) in data.calculationResult.criticalErrors"
                                                :key="index"
                                                variant="outlined"
                                                class="mb-2 pa-3"
                                            >
                                                <div class="d-flex align-start">
                                                    <v-icon color="error" class="mr-2">mdi-alert</v-icon>
                                                    <div class="text-body-2 text-error flex-grow-1">
                                                        {{ error }}
                                                    </div>
                                                </div>
                                            </v-card>
                                        </div>
                                    </v-expansion-panel-text>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </div>

                        <v-row class="mb-3">
                            <v-col cols="12" md="6">
                                <v-card variant="outlined" class="pa-3">
                                    <div class="text-h6 mb-2">Processing Summary</div>
                                    <div class="text-body-1">
                                        <div
                                            v-if="
                                                data.calculationResult.daysProcessed &&
                                                data.calculationResult.daysProcessed > 0
                                            "
                                            class="mb-2 pa-2"
                                            style="background-color: #f5f5f5; border-radius: 4px"
                                        >
                                            <strong>Days Processed:</strong>
                                            {{ data.calculationResult.daysProcessed }}
                                            <span v-if="data.calculationResult.daysSucceeded > 0" class="text-success">
                                                ({{ data.calculationResult.daysSucceeded }} succeeded<span
                                                    v-if="data.calculationResult.daysFailed > 0"
                                                    >,
                                                </span>
                                            </span>
                                            <span v-if="data.calculationResult.daysFailed > 0" class="text-error">
                                                {{ data.calculationResult.daysFailed }} failed</span
                                            >
                                            <span v-if="data.calculationResult.daysSucceeded > 0">)</span>
                                        </div>
                                        <div>
                                            <strong>Total Orders Processed:</strong>
                                            {{ data.calculationResult.totalProcessed }}
                                        </div>
                                        <div class="text-success">
                                            <strong>Successful Calculations:</strong>
                                            {{ data.calculationResult.successCount }}
                                        </div>
                                        <div v-if="data.calculationResult.failedCount > 0" class="text-error">
                                            <strong>Failed Calculations:</strong>
                                            {{ data.calculationResult.failedCount }}
                                        </div>
                                        <div>
                                            <strong>Processing Time:</strong>
                                            {{ (data.calculationResult.processingTimeMs / 1000).toFixed(2) }}s
                                        </div>
                                    </div>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Show failed orders if any -->
                        <div
                            v-if="
                                data.calculationResult.failedCount > 0 && data.calculationResult.failedOrders.length > 0
                            "
                            class="failed-orders-section"
                        >
                            <v-expansion-panels variant="accordion">
                                <v-expansion-panel>
                                    <v-expansion-panel-title class="text-error">
                                        <v-icon class="mr-2">mdi-alert-circle</v-icon>
                                        Failed Orders ({{ data.calculationResult.failedOrders.length }})
                                    </v-expansion-panel-title>
                                    <v-expansion-panel-text>
                                        <div class="failed-orders-list">
                                            <div
                                                v-for="failedOrder in data.calculationResult.failedOrders"
                                                :key="failedOrder.orderId"
                                                class="failed-order-item"
                                            >
                                                <v-card variant="outlined" class="mb-2">
                                                    <v-card-text class="pa-3">
                                                        <v-row no-gutters>
                                                            <v-col cols="12" sm="6" md="3">
                                                                <div class="text-caption text-grey-darken-1">
                                                                    Order Number
                                                                </div>
                                                                <div class="text-body-2 font-weight-medium">
                                                                    {{ failedOrder.orderNumber }}
                                                                </div>
                                                            </v-col>
                                                            <v-col cols="12" sm="6" md="2">
                                                                <div class="text-caption text-grey-darken-1">
                                                                    Marketplace
                                                                </div>
                                                                <v-chip size="small" color="primary" variant="outlined">
                                                                    {{ failedOrder.marketplaceCode }}
                                                                </v-chip>
                                                            </v-col>
                                                            <v-col cols="12" md="4">
                                                                <div class="text-caption text-grey-darken-1">
                                                                    Failure Reason
                                                                </div>
                                                                <div class="text-body-2 text-error">
                                                                    {{ failedOrder.reason }}
                                                                </div>
                                                            </v-col>
                                                            <v-col cols="12" md="3">
                                                                <div class="text-caption text-grey-darken-1">
                                                                    Order ID
                                                                </div>
                                                                <div class="text-body-2 font-mono text-grey-darken-2">
                                                                    {{ failedOrder.orderId }}
                                                                </div>
                                                            </v-col>
                                                        </v-row>
                                                    </v-card-text>
                                                </v-card>
                                            </div>
                                        </div>
                                    </v-expansion-panel-text>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </div>
                    </div>

                    <!-- when calculate result error, show error message -->
                    <div v-if="data.resultFail" class="error-section">
                        <v-alert type="error" variant="tonal">
                            <v-alert-title>Calculation Failed</v-alert-title>
                            {{ data.errorAlertMessage }}
                        </v-alert>
                    </div>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive } from 'vue';
import * as _ from 'lodash';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { FilterHelper } from '../../utils/FilterHelper';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { GrossProfitLabelTextHelper } from '../../utils/labelTextHelper/GrossProfitLabelTextHelper';
import { Formatter } from '../../utils/Formatter';
import { PathDefinition } from '../../utils/PathDefinition';
import DateRangeSelect from '../../components/DateRangeSelect.vue';
import AlertMessage from '../../components/AlertMessage.vue';
import ExportToCsv from '../../components/ExportToCsv.vue';
import { GrossProfitHeaderHelper } from '../../utils/headerHelper/GrossProfitHeaderHelper';

dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(utc);

export default defineComponent({
    name: 'CalculateGrossProfit',

    components: {
        ExportToCsv,
        AlertMessage,
        DateRangeSelect
    },

    setup() {
        const data = reactive({
            panel: [0],
            processing: false,
            resultSuccess: false,
            resultFail: false,
            errorAlertMessage: '',
            calculateGrossProfitRequestDefault: {},
            calculateGrossProfitRequest: {
                dateOrderOn: {
                    from: null,
                    to: null
                }
            },
            calculationResult: {
                status: '',
                successCount: 0,
                failedCount: 0,
                totalProcessed: 0,
                failedOrderIds: [] as string[], // Keep for backward compatibility
                failedOrders: [] as any[], // New detailed information
                processingTimeMs: 0,
                criticalErrors: [] as string[],
                daysProcessed: 0,
                daysSucceeded: 0,
                daysFailed: 0
            },
            requestItem: {
                trackingNumber: ''
            },
            validation: {
                hasErrors: false
            }
        });
        return {
            datepickerPreviewFormat: Formatter.defaultDatepickerPreviewFormat,
            formLabelText: GrossProfitLabelTextHelper.getLabelText,
            actionHeaderItem: GrossProfitHeaderHelper.getActionHeaderItem(),
            headers: [...GrossProfitHeaderHelper.getHeaders()],
            data
        };
    },
    computed: {},
    async created() {
        this.data.calculateGrossProfitRequestDefault = _.cloneDeep(this.data.calculateGrossProfitRequest);
    },
    methods: {
        onResetClick() {
            this.data.calculateGrossProfitRequest = _.cloneDeep(this.data.calculateGrossProfitRequestDefault);
            this.data.calculationResult = {
                status: '',
                successCount: 0,
                failedCount: 0,
                totalProcessed: 0,
                failedOrderIds: [],
                failedOrders: [],
                processingTimeMs: 0,
                criticalErrors: [],
                daysProcessed: 0,
                daysSucceeded: 0,
                daysFailed: 0
            };
            this.data.resultSuccess = false;
            this.data.resultFail = false;
        },
        async onCalculateClick() {
            if (this.data.validation.hasErrors) {
                this.data.errorAlertMessage = 'Please fix the date range errors before calculating';
                this.$refs.errorAlert.showAlert();
                return;
            }

            const request = {
                dateOrderOnFrom: Formatter.convertDateToStandardFormatDateStr(
                    this.data.calculateGrossProfitRequest.dateOrderOn.from
                ),
                dateOrderOnTo: Formatter.convertDateToStandardFormatDateStr(
                    this.data.calculateGrossProfitRequest.dateOrderOn.to
                )
            };
            const requestUrl = `/api/staff/grossProfit/calculateByDateRange`;
            this.data.processing = true;
            this.data.resultSuccess = false;
            this.data.resultFail = false;

            try {
                const response = await fetchWrapper.post(requestUrl, request);
                if (response.success) {
                    // Handle the new CalculateGrossProfitResult structure
                    const result = response.result;
                    const failedCount = result.failedCount || 0;
                    const successCount = result.successCount || 0;
                    const criticalErrors = result.criticalErrors || [];

                    // Compute status based on results
                    let status = '';
                    if (failedCount === 0 && criticalErrors.length === 0) {
                        status = 'FULL_SUCCESS';
                    } else if (successCount > 0 && (failedCount > 0 || criticalErrors.length > 0)) {
                        status = 'PARTIAL_SUCCESS';
                    } else {
                        status = 'FULL_FAILURE';
                    }

                    this.data.calculationResult = {
                        status: status,
                        successCount: successCount,
                        failedCount: failedCount,
                        totalProcessed: result.totalProcessed || 0,
                        failedOrderIds: result.failedOrderIds || [],
                        failedOrders: result.failedOrders || [],
                        processingTimeMs: result.processingTimeMs || 0,
                        criticalErrors: criticalErrors,
                        daysProcessed: result.daysProcessed || 0,
                        daysSucceeded: result.daysSucceeded || 0,
                        daysFailed: result.daysFailed || 0
                    };
                    this.data.processing = false;
                    this.data.resultSuccess = true;
                    return response.result;
                } else {
                    this.data.processing = false;
                    this.data.resultFail = true;
                    this.data.errorAlertMessage = response.errors || 'Calculation failed';
                    this.$refs.errorAlert.showAlert();
                }
            } catch (error) {
                this.data.processing = false;
                this.data.resultFail = true;
                this.data.errorAlertMessage = 'An unexpected error occurred during calculation';
                this.$refs.errorAlert.showAlert();
            }
        },
        onDateRangeValidationChange(validationStatus: { hasErrors: boolean }) {
            this.data.validation.hasErrors = validationStatus.hasErrors;
        },
        async onListGrossProfitClick(item: any) {
            await this.$router.push({ path: PathDefinition.listGrossProfit });
        }
    }
});
</script>

<style>
.validation-error {
    color: red;
}

.processing-section {
    display: flex;
    align-items: center;
    padding: 20px;
    text-align: center;
}

.processing-text {
    font-size: 16px;
    color: #1976d2;
}

.success-section {
    margin-top: 16px;
}

.error-section {
    margin-top: 16px;
}

.critical-errors-section {
    margin-top: 16px;
}

.critical-errors-list {
    max-height: 400px;
    overflow-y: auto;
}

.failed-orders-section {
    margin-top: 16px;
}

.failed-orders-list {
    max-height: 400px;
    overflow-y: auto;
}

.failed-order-item {
    transition: all 0.2s ease-in-out;
}

.failed-order-item:hover {
    transform: translateY(-1px);
}

.font-mono {
    font-family: 'Courier New', Courier, monospace;
    font-size: 0.85em;
}
</style>
